import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { ExportBar } from '../components/Filters';

/**
 * ProductPopover — A dropdown-style popover for handling large CLOB data
 * without affecting table column widths or row heights.
 */
function ProductPopover({ text }) {
  const [show, setShow] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setShow(false);
    };
    if (show) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show]);

  if (!text || text === '—' || text === 'None' || text === 'null') return <span>—</span>;

  return (
    <div className="product-popover-container" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <div
        style={{
          maxWidth: '180px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.9em',
          color: '#555',
        }}
      >
        {text}
      </div>
      <button
        className="popover-trigger"
        onClick={() => setShow(!show)}
        style={{
          marginLeft: '8px',
          cursor: 'pointer',
          border: '1px solid #d1d5db',
          background: '#f3f4f6',
          borderRadius: '4px',
          padding: '1px 5px',
          fontSize: '10px',
          flexShrink: 0,
        }}
      >
        {show ? '✕' : '•••'}
      </button>

      {show && (
        <div
          ref={popoverRef}
          className="product-dropdown-card"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 9999,
            width: '320px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            padding: '12px',
            marginTop: '8px',
            maxHeight: '250px',
            overflowY: 'auto',
          }}
        >
          <div style={{ fontWeight: '600', fontSize: '12px', color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
            Products Dealing
          </div>
          <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.5', whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </div>
        </div>
      )}
    </div>
  );
}

function splitValues(value) {
  if (!value) return [];
  return String(value)
    .split('|')
    .map(part => part.trim())
    .filter(part => part && part !== '—' && part !== 'None' && part !== 'null');
}

function MultiValueCell({ value, type = 'text' }) {
  const items = splitValues(value);

  if (items.length === 0) return <span>—</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#374151' }}>
      {items.map((item, idx) =>
        type === 'email' ? (
          <a
            key={`${item}-${idx}`}
            href={`mailto:${item}`}
            style={{ color: '#2563eb', textDecoration: 'none', wordBreak: 'break-word' }}
          >
            {item}
          </a>
        ) : (
          <span key={`${item}-${idx}`} style={{ wordBreak: 'break-word' }}>
            {item}
          </span>
        )
      )}
    </div>
  );
}

function ContactDesignationCell({ contactPerson, designation }) {
  const contacts = splitValues(contactPerson);
  const titles = splitValues(designation);
  const rows = Math.max(contacts.length, titles.length);

  if (rows === 0) return <span>—</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#374151' }}>
      {Array.from({ length: rows }).map((_, idx) => {
        const contact = contacts[idx] || contacts[0] || '';
        const title = titles[idx] || titles[0] || '';
        const text = [contact, title].filter(Boolean).join(' - ');
        return (
          <span key={`${text}-${idx}`} style={{ wordBreak: 'break-word' }}>
            {text || '—'}
          </span>
        );
      })}
    </div>
  );
}

export default function Suppliers() {
  const { can } = useAuth();

  const getSaved = (k, d) => {
    try {
      const v = sessionStorage.getItem('suppliers_' + k);
      return v !== null ? JSON.parse(v) : d;
    } catch {
      return d;
    }
  };

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => getSaved('page', 1));
  const [search, setSearch] = useState(() => getSaved('search', ''));
  const [dSearch, setDSearch] = useState(() => getSaved('search', ''));
  const [loading, setLoading] = useState(true);
  const [hsCodeOptions, setHsCodeOptions] = useState([]);

  const [colCompany, setColCompany] = useState(() => getSaved('company', ''));
  const [colHsCode, setColHsCode] = useState(() => getSaved('hscode', ''));
  const [colProduct, setColProduct] = useState(() => getSaved('product', ''));
  const [contactInfo, setContactInfo] = useState(() => getSaved('contactInfo', ''));
   const [hasContactPerson, setHasContactPerson] = useState(() => getSaved('hasContactPerson', ''));
   const [colContactPerson, setColContactPerson] = useState(() => getSaved('contactPerson', ''));
  const [colDesignation, setColDesignation] = useState(() => getSaved('designation', ''));
  const [colPhone, setColPhone] = useState(() => getSaved('phone', ''));
  const [colEmail, setColEmail] = useState(() => getSaved('email', ''));

  const loadHsCodeOptions = useCallback(async () => {
    try {
      const data = await api.suppliers.filterOptions();
      const normalized = (Array.isArray(data.hsOptions) ? data.hsOptions : [])
        .map(opt => ({
          code: String(opt.code ?? '').trim(),
          count: Number(opt.count) || 0,
          product: String(opt.product ?? '').trim(),
        }))
        .filter(opt => opt.code);

      const deduped = Array.from(
        new Map(normalized.map(opt => [opt.code, opt])).values()
      ).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

      setHsCodeOptions(deduped);
    } catch (e) {
      console.error('Failed to fetch HS code options:', e);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('suppliers_page', JSON.stringify(page));
    sessionStorage.setItem('suppliers_search', JSON.stringify(search));
    sessionStorage.setItem('suppliers_company', JSON.stringify(colCompany));
    sessionStorage.setItem('suppliers_hscode', JSON.stringify(colHsCode));
    sessionStorage.setItem('suppliers_product', JSON.stringify(colProduct));
    sessionStorage.setItem('suppliers_contactInfo', JSON.stringify(contactInfo));
    sessionStorage.setItem('suppliers_hasContactPerson', JSON.stringify(hasContactPerson));
    sessionStorage.setItem('suppliers_contactPerson', JSON.stringify(colContactPerson));
    sessionStorage.setItem('suppliers_designation', JSON.stringify(colDesignation));
    sessionStorage.setItem('suppliers_phone', JSON.stringify(colPhone));
    sessionStorage.setItem('suppliers_email', JSON.stringify(colEmail));
    }, [page, search, colCompany, colHsCode, colProduct, contactInfo, hasContactPerson, colContactPerson, colDesignation, colPhone, colEmail]);

  const limit = 50;

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== dSearch) {
        setDSearch(search);
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, dSearch]);

  const buildParams = useCallback(() => {
    const p = { page, limit };
    if (dSearch) p.search = dSearch;
    if (colCompany) p.company = colCompany;
    if (colHsCode) p.hscode = colHsCode;
    if (colProduct) p.product = colProduct;
    if (contactInfo) p.contactInfo = contactInfo;
    if (hasContactPerson) p.hasContactPerson = hasContactPerson;
    if (colContactPerson) p.contactPerson = colContactPerson;
    if (colDesignation) p.designation = colDesignation;
    if (colPhone) p.phone = colPhone;
    if (colEmail) p.email = colEmail;
    return p;
    }, [page, dSearch, colCompany, colHsCode, colProduct, contactInfo, hasContactPerson, colContactPerson, colDesignation, colPhone, colEmail]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.suppliers.records(buildParams());
      setRecords(res.rows);
      setTotal(res.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    loadHsCodeOptions();
  }, [loadHsCodeOptions]);

  useEffect(() => {
    const refreshOnFocus = () => {
      loadHsCodeOptions();
    };

    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') {
        loadHsCodeOptions();
      }
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisible);

    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [loadHsCodeOptions]);

  useEffect(() => {
    setPage(1);
    }, [colCompany, colHsCode, colProduct, contactInfo, hasContactPerson, colContactPerson, colDesignation, colPhone, colEmail]);

  const handleExport = (fmt) => {
    const p = buildParams();
    delete p.page;
    delete p.limit;
    p.format = fmt;
    api.suppliers.export(p);
  };

  const clearAll = () => {
    setSearch('');
    setDSearch('');
    setColCompany('');
    setColHsCode('');
    setColProduct('');
    setContactInfo('');
     setHasContactPerson('');
    setColContactPerson('');
    setColDesignation('');
    setColPhone('');
    setColEmail('');
  };

    const hasFilters = dSearch || colCompany || colHsCode || colProduct || contactInfo || hasContactPerson || colContactPerson || colDesignation || colPhone || colEmail;
  const fmt = n => (n == null ? '—' : Number(n).toLocaleString('en-IN'));
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Prospect Suppliers</h1>
          <p className="page-desc">Consolidated Export Intelligence (Turmeric, Sesame, Tahini)</p>
        </div>
        {hasFilters && (
          <button className="btn-clear-all" onClick={clearAll}>✕ Reset All</button>
        )}
      </div>

      <div className="filter-section" style={{ marginTop: '0.75rem' }}>
        <div className="filter-row">
          <input
            className="search-input"
            placeholder="Global Search (Company, Product, Location, Contact)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '10px', alignItems: 'center' }}>
          <div className="filter-group">
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#666' }}>CONTACT INFO</label>
            <select
              className="date-select"
              style={{ height: '38px', minWidth: '220px' }}
              value={contactInfo}
              onChange={e => setContactInfo(e.target.value)}
            >
              <option value="">All</option>
              <option value="email">Has Email</option>
              <option value="phone">Has Phone</option>
              <option value="either">Either Email or Phone</option>
              <option value="both">Both Email & Phone</option>
            </select>
          </div>
           <div className="filter-group">
             <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#666' }}>HAS CONTACT PERSON</label>
             <select
               className="date-select"
               style={{ height: '38px', minWidth: '160px' }}
               value={hasContactPerson}
               onChange={e => setHasContactPerson(e.target.value)}
             >
               <option value="">All</option>
               <option value="yes">Yes</option>
               <option value="no">No</option>
             </select>
           </div>
        </div>
      </div>

      <ExportBar onExport={handleExport} total={total} canExport={can('export')} />

      <div className="table-card" style={{ marginTop: '12px', overflow: 'visible' }}>
        {loading ? (
          <div className="table-loading"><div className="spinner" /></div>
        ) : (
          <>
            <div className="table-meta-bar">
              <span><strong>{fmt(total)}</strong> suppliers matching criteria</span>
            </div>

            <div className="table-scroll" style={{ overflowX: 'auto', overflowY: 'visible', width: '100%' }}>
               <table className="data-table" style={{ borderCollapse: 'separate', borderSpacing: '0', minWidth: '1800px', tableLayout: 'fixed', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: '220px' }}>Company</th>
                    <th style={{ width: '240px' }}>Products</th>
                    <th style={{ width: '120px' }}>HS Code</th>
                    <th style={{ width: '160px' }}>Location</th>
                    <th style={{ width: '240px' }}>Contact Person - Designation</th>
                    <th style={{ width: '170px' }}>Phone</th>
                    <th style={{ width: '220px' }}>Email</th>
                    <th style={{ textAlign: 'center', width: '80px' }}>Web/Social</th>
                  </tr>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ width: '220px', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                      <input
                        type="text"
                        placeholder="Company..."
                        value={colCompany}
                        onChange={e => setColCompany(e.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </th>
                    <th style={{ width: '240px', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                      {/* Product filter hidden - use global search */}
                    </th>
                    <th style={{ width: '120px', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                      <select
                        value={colHsCode}
                        onChange={e => setColHsCode(e.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      >
                        <option value="">All HS Codes</option>
                        {hsCodeOptions.map((opt, idx) => (
                          <option key={idx} value={opt.code}>
                            {opt.code}{opt.count ? ` (${opt.count})` : ''}{opt.product ? ` — ${opt.product.split('|')[0]}` : ''}
                          </option>
                        ))}
                      </select>
                    </th>
                    <th style={{ width: '160px', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                      {/* Location filter - use global search */}
                    </th>
                    <th style={{ width: '240px', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                      <input
                        type="text"
                        placeholder="Contact/Title..."
                        value={colContactPerson}
                        onChange={e => setColContactPerson(e.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </th>
                    <th style={{ width: '170px', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                      <input
                        type="text"
                        placeholder="Phone..."
                        value={colPhone}
                        onChange={e => setColPhone(e.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </th>
                    <th style={{ width: '220px', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                      <input
                        type="text"
                        placeholder="Email..."
                        value={colEmail}
                        onChange={e => setColEmail(e.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </th>
                    <th style={{ width: '80px', textAlign: 'center', padding: '8px' }}>
                      {hasFilters && (
                        <button onClick={clearAll} style={{ fontSize: '11px', padding: '4px 8px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}>
                          ✕ Clear
                        </button>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <td className="td-company">
                        <div style={{ fontWeight: '600', color: '#111827' }}>{r.COMPANY_NAME || '—'}</div>
                      </td>
                      <td style={{ position: 'relative' }}>
                        <ProductPopover text={r.PRODUCTS_DEALING} />
                      </td>
                      <td>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: r.HS_CODE === '09103020' ? '#fff7ed' : '#f0fdf4',
                            color: r.HS_CODE === '09103020' ? '#c2410c' : '#15803d',
                            border: `1px solid ${r.HS_CODE === '09103020' ? '#fdba74' : '#bbf7d0'}`,
                          }}
                        >
                          {r.HS_CODE || '—'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.9em' }}>
                        {r.CITY ? `${r.CITY}, ${r.COUNTRY}` : r.COUNTRY || '—'}
                      </td>
                      <td>
                        <ContactDesignationCell contactPerson={r.DECISION_MAKER} designation={r.BUSINESS_TITLE} />
                      </td>
                      <td>
                        <MultiValueCell value={r.PHONE} />
                      </td>
                      <td>
                        <MultiValueCell value={r.GMAIL} type="email" />
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                          {r.WEBSITE && <a href={r.WEBSITE} target="_blank" rel="noreferrer" title="Visit Website" style={{ textDecoration: 'none' }}>🌐</a>}
                          {r.LINKED_IN_LINK && <a href={r.LINKED_IN_LINK} target="_blank" rel="noreferrer" title="LinkedIn Profile" style={{ textDecoration: 'none' }}>🔗</a>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Previous</button>
              <span className="page-info">Page <strong>{page}</strong> of {totalPages || 1}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}